'use client'

import { useMemo } from 'react'
import type { Episode, CalendarEvent, EventType } from '@/types'
import { formatFullDate, getMonthName } from '@/lib/date'

export interface CalendarListViewProps {
  episodes: Episode[]
  currentMonth: string // YYYY-MM format
  today: string // YYYY-MM-DD format
  onMonthChange: (month: string) => void
  onEventClick?: (event: CalendarEvent) => void
}

interface EventTypeConfig {
  label: string
  dotColor: string
  bgColor: string
  textColor: string
}

const EVENT_TYPE_CONFIG: Record<EventType, EventTypeConfig> = {
  recording: {
    label: 'Recording',
    dotColor: 'bg-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-700 dark:text-blue-300',
  },
  'air-date': {
    label: 'Air Date',
    dotColor: 'bg-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-700 dark:text-green-300',
  },
  deadline: {
    label: 'Deadline',
    dotColor: 'bg-amber-500',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    textColor: 'text-amber-700 dark:text-amber-300',
  },
}

interface DateGroup {
  date: string
  isToday: boolean
  events: CalendarEvent[]
}

export function CalendarListView({
  episodes,
  currentMonth,
  today,
  onMonthChange,
  onEventClick,
}: CalendarListViewProps) {
  const [year, month] = currentMonth.split('-').map(Number)

  // Generate calendar events from episodes
  const events = useMemo(() => {
    const calendarEvents: CalendarEvent[] = []

    episodes.forEach((episode) => {
      // Recording event
      calendarEvents.push({
        id: `${episode.id}-recording`,
        episodeId: episode.id,
        type: 'recording',
        date: episode.recordingDate,
        title: episode.title,
        episodeNumber: episode.number,
        guestNames: episode.guestNames,
      })

      // Air date event
      calendarEvents.push({
        id: `${episode.id}-air`,
        episodeId: episode.id,
        type: 'air-date',
        date: episode.airDate,
        title: episode.title,
        episodeNumber: episode.number,
        guestNames: episode.guestNames,
      })
    })

    return calendarEvents
  }, [episodes])

  // Group events by date and filter to current month
  const dateGroups = useMemo(() => {
    const monthPrefix = `${year}-${String(month).padStart(2, '0')}`

    // Filter events for current month
    const monthEvents = events.filter(e => e.date.startsWith(monthPrefix))

    // Group by date
    const groupMap = new Map<string, CalendarEvent[]>()
    monthEvents.forEach(event => {
      const existing = groupMap.get(event.date) || []
      groupMap.set(event.date, [...existing, event])
    })

    // Convert to array and sort by date
    const groups: DateGroup[] = Array.from(groupMap.entries())
      .map(([date, dateEvents]) => ({
        date,
        isToday: date === today,
        events: dateEvents.sort((a, b) => {
          // Sort recording before air-date for same episode
          if (a.type === 'recording' && b.type === 'air-date') return -1
          if (a.type === 'air-date' && b.type === 'recording') return 1
          // Secondary sort by episode number for stable ordering
          return a.episodeNumber - b.episodeNumber
        }),
      }))
      .sort((a, b) => a.date.localeCompare(b.date))

    return groups
  }, [events, year, month, today])

  const handlePrevMonth = () => {
    const newMonth = month === 1 ? 12 : month - 1
    const newYear = month === 1 ? year - 1 : year
    onMonthChange(`${newYear}-${String(newMonth).padStart(2, '0')}`)
  }

  const handleNextMonth = () => {
    const newMonth = month === 12 ? 1 : month + 1
    const newYear = month === 12 ? year + 1 : year
    onMonthChange(`${newYear}-${String(newMonth).padStart(2, '0')}`)
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
      {/* Header with month/year and navigation */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-zinc-200 dark:border-zinc-800">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          {getMonthName(month - 1)} {year}
        </h2>
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrevMonth}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Previous month"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Next month"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-sm text-zinc-600 dark:text-zinc-400">Recording</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-sm text-zinc-600 dark:text-zinc-400">Air Date</span>
        </div>
      </div>

      {/* Event list grouped by date */}
      <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
        {dateGroups.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-zinc-500 dark:text-zinc-400">No events scheduled for this month</p>
          </div>
        ) : (
          dateGroups.map((group) => (
            <div key={group.date}>
              {/* Date header - sticky */}
              <div className="sticky top-0 z-10 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    {formatFullDate(group.date)}
                  </span>
                  {group.isToday && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#FF6F00] text-white">
                      Today
                    </span>
                  )}
                </div>
              </div>

              {/* Events for this date */}
              <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {group.events.map((event) => {
                  const typeConfig = EVENT_TYPE_CONFIG[event.type]
                  return (
                    <button
                      key={event.id}
                      onClick={() => onEventClick?.(event)}
                      className="w-full px-4 py-3 flex items-start gap-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors text-left min-h-[44px]"
                    >
                      {/* Type dot */}
                      <span className={`w-3 h-3 rounded-full ${typeConfig.dotColor} flex-shrink-0 mt-1.5`} />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-[#FF6F00]">
                            Ep {event.episodeNumber}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeConfig.bgColor} ${typeConfig.textColor}`}>
                            {typeConfig.label}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mt-0.5 truncate">
                          {event.title}
                        </p>
                        {event.guestNames.length > 0 && (
                          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
                            with {event.guestNames.join(', ')}
                          </p>
                        )}
                      </div>

                      {/* Chevron */}
                      <svg className="w-5 h-5 text-zinc-400 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
